import { Badge, BadgeProps } from '@ff-it/ui';
import { type ReactElement, ReactNode, createContext, useCallback, useContext } from 'react';
import { fetcher, useSession } from 'services';
import useSWR from 'swr';
import invariant from 'tiny-invariant';

const TIMEOUT = 1000 * 60 * 3;

const emptyState = {
  open_tickets: 0,
  calendar_events: 0,
  unseen_notifications: 0,
  late_tasks: 0,
} as const;

type Indicators = typeof emptyState;

type IndicatorsContextType = {
  indicators: Indicators;
  invalidate: (key?: keyof Indicators) => Promise<void>;
};

const IndicatorsContext = createContext<IndicatorsContextType | undefined>(undefined);

export function IndicatorsProvider({ children }: { children: ReactNode }): ReactElement {
  const agency = useSession().scope?.slug;

  const { data, mutate } = useSWR<Indicators>(
    agency ? { method: 'GET', url: 'core/indicators/', _agency: agency } : undefined,
    { refreshInterval: TIMEOUT, fallbackData: emptyState },
  );

  const invalidate = useCallback(
    async (key?: keyof Indicators) => {
      if (!key) {
        await mutate();
        return;
      }
      await mutate(
        async (state) => {
          if (state) {
            const counter = await fetcher<number>({ method: 'GET', url: `core/indicators/${key}/` });
            return { ...state, [key]: counter };
          }
          return state;
        },
        { revalidate: false },
      );
    },
    [mutate],
  );

  return (
    <IndicatorsContext.Provider
      value={{
        indicators: data || emptyState,
        invalidate,
      }}
    >
      {children}
    </IndicatorsContext.Provider>
  );
}

export function useIndicatorsContext(): IndicatorsContextType {
  const ctx = useContext(IndicatorsContext);
  invariant(ctx);
  return ctx;
}

type NotificationCounterBadgeProps = {
  counter: keyof Indicators;
  children?: ReactNode;
} & Pick<BadgeProps, 'variant' | 'placement'>;

export function IndicatorBadge({ counter, children, ...rest }: NotificationCounterBadgeProps): ReactElement | null {
  const count = useIndicatorsContext().indicators[counter];

  return (
    <Badge count={count} {...rest}>
      {children}
    </Badge>
  );
}

export function useIndicatorInvalidate(): IndicatorsContextType['invalidate'] {
  return useIndicatorsContext().invalidate;
}
